/**
 * KVS Samagam RTK Query Endpoints
 * 
 * Module-level API file — all endpoints inject into the central baseApi.
 * Inherits auth headers + 401 handling automatically.
 * 
 * Usage:
 *   import { useGetSamagamTotalSchoolsQuery } from '../../../../store/api';
 *   const { data, isLoading } = useGetSamagamTotalSchoolsQuery();
 */

import { baseApi } from '../../../../store/api/baseApi';
import type {
  LibrarySummaryResponse,
  DigitalLibraryResponse,
  AutomatedSystemsResponse,
  AvgBooksResponse,
  BestLibrary,
  LibraryUpgrade,
  CwsnStudentResponse,
  CwsnTeacherResponse,
  RampResponse,
  RegionalGroundsResponse,
  NationalGroundsResponse,
  SportsByCategoryResponse,
  QualityDataResponse,
  IndoorOutdoorResponse,
  TopFacilityKvsResponse,
} from './api';

// ──────────────────────────────────────────────────────────
// Safety Analysis Types
// ──────────────────────────────────────────────────────────

export interface SafetyBreakdownItem {
  Total: number;
  [key: string]: any;
}

export interface SafetyCategoryRow {
  Category: string;
  'PM-SHRI Schools': number;
  'Non-PM-SHRI Schools': number;
  'Total Schools': number;
  Breakdown?: SafetyBreakdownItem[];
}

export type SafetyTotalDetailsResponse = SafetyCategoryRow[];

export interface SafetySchoolRecord {
  schoolName: string;
  schoolID: number;
  roName: string;
  stateName: string;
  districtName: string;
  isPMShree: boolean;
  // Building safety
  status: string;
  buildingSafety: string;
  buildingSafetyCertificate: string;
  agency: string;
  validFrom: string;
  validTo: string;
  buildingValidFrom: string;
  buildingValidTo: string;
  // Fire safety
  fireSafety: string;
  fireSafetyCertificate: string;
  fireAgencyOther: string;
  fireValidFrom: string;
  fireValidTo: string;
}

export interface SafetyCertificateSummary {
  totalSchools: number;
  available: number;
  notAvailable: number;
  pmShri: number;
  nonPmShri: number;
}

export interface SafetyCertificateDetailsResponse {
  summary: SafetyCertificateSummary;
  schools: SafetySchoolRecord[];
}

// ──────────────────────────────────────────────────────────
// Special Labs Types
// ──────────────────────────────────────────────────────────

export interface SpecialLabsSchoolRecord {
  schoolName: string;
  schoolID: number;
  roName: string;
  stateName: string;
  districtName: string;
  isPMShree: boolean;
  atlLab: string;              // 'Available' | 'Not Available'
  atlFundedBy: string;
  roboticsLab: string;
  robotFundedBy: string;
  arvrLab: string;
  arvrFundedBy: string;
  languageLab: string;
  languageLabFunctional: string; // 'Functional' | 'Non-Functional'
  amcCovered: string;
  totalComputers: number;
}

export interface SpecialLabsSummary {
  totalSchools: number;
  atlAvailable: number;
  roboticsAvailable: number;
  arvrAvailable: number;
  languageLabAvailable: number;
  totalFunctionalLabs: number;
  totalNonFunctionalLabs: number;
  amcProvided: number;
  totalComputersInLabs: number;
  pmShri: number;
  nonPmShri: number;
}

export interface SpecialLabsDetailsResponse {
  summary: SpecialLabsSummary;
  schools: SpecialLabsSchoolRecord[];
}

// ──────────────────────────────────────────────────────────
// NCC Profile Types
// ──────────────────────────────────────────────────────────

export interface NCCSchoolRecord {
  schoolID: number;
  schoolName: string;
  roName: string;          // region / RO name
  stateName: string;
  districtName: string;
  isPMShree: boolean;
  isNccAvailable: string;  // 'Available' | other
  nccGroup: string;        // 'Army' | 'Naval' | 'Air Force'
  avaliableWing: string;   // 'Sr. Wing' | 'Jr. Wing'
  totalBoyCadet: number;
  totalGirlCadet: number;
  totalCadets: number;
  anoCto: string;          // 'ANO' | 'CTO' | ''
  anoCtoRank: string;
}

export interface NCCProfileSummary {
  totalSchools: number;
  nccAvailable: number;
  nccNotAvailable: number;
  armyWingUnits: number;
  airForceWingUnits: number;
  navyWingUnits: number;
  anoCount: number;
  ctoCount: number;
  totalNccOfficers: number;
  totalBoyCadets: number;
  totalGirlCadets: number;
  pmShri: number;
  nonPmShri: number;
}

export interface NCCProfileDetailsResponse {
  summary: NCCProfileSummary;
  schools: NCCSchoolRecord[];
}

// ──────────────────────────────────────────────────────────
// Green Building Types
// ──────────────────────────────────────────────────────────

interface GreenBuildingRoRow {
  roID: number;
  roName: string;
  pmshri: number;
  nonpmshri: number;
  total: number;
}

interface GreenBuildingCategorySummary {
  totalROs: number;
  pmshri: number;
  nonpmshri: number;
  total: number;
}

export interface GreenBuildingCategoryResponse {
  summary: GreenBuildingCategorySummary;
  data: GreenBuildingRoRow[];
}

// ──────────────────────────────────────────────────────────
// Query argument types
// ──────────────────────────────────────────────────────────

interface TopSchoolsInfraArgs {
  wise?: string;
  order?: 'asc' | 'desc';
  page?: number;
}

interface EnrollmentTrendArgs {
  years?: number;
}

interface KPIByCategoryArgs {
  category: string;
}

interface KPIDataArgs {
  category: string;
  kpi: string;
}
interface GreenBuildingCategoryArgs {
  category: 'isRainWaterSystem' | 'solarPowerPlant' | 'energyEfficientElectricSystem' | 'availSolarLight';
}
// ──────────────────────────────────────────────────────────
// Inject endpoints into shared baseApi
// ──────────────────────────────────────────────────────────

export const kvsSamagamApi = baseApi.injectEndpoints({
  endpoints: (build) => ({

    // ============================================
    // Core / Shared Endpoints
    // ============================================

    /** Total schools count */
    getSamagamTotalSchools: build.query<number | null, void>({
      query: () => '/kvis/total',
      transformResponse: (response: any) => {
        if (response?.success && response.data && typeof response.data.schoolCount === 'number') {
          return response.data.schoolCount;
        }
        return null;
      },
      providesTags: ['Samagam'],
      keepUnusedDataFor: 600, // School count rarely changes - cache for 10 mins
    }),

    /** All states for dropdowns */
    getSamagamAllStates: build.query<any[], void>({
      query: () => '/shared/allstate',
      transformResponse: (response: any) => {
        if (response?.success && Array.isArray(response.data)) {
          return response.data;
        }
        return [];
      },
      providesTags: ['Samagam'],
      keepUnusedDataFor: 3600, // States rarely change
    }),

    /** All regions for dropdowns (excluding Abroad KV) */
    getSamagamAllRegions: build.query<any[], void>({
      query: () => '/shared/allros',
      transformResponse: (response: any) => {
        if (response?.success && Array.isArray(response.data)) {
          return response.data.filter((region: any) => region.roName !== 'Abroad KV');
        }
        return [];
      },
      providesTags: ['Samagam'],
      keepUnusedDataFor: 3600,
    }),

    /** All categories for dropdowns */
    getSamagamAllCategories: build.query<Array<{ key: string; label: string }>, void>({
      query: () => '/shared/category',
      transformResponse: (response: any) => {
        if (response?.success && response.data && typeof response.data === 'object') {
          return Object.entries(response.data)
            .map(([_index, value]) => {
              let profileName: string;
              let displayLabel: string;

              if (typeof value === 'string') {
                profileName = value;
                displayLabel = value
                  .replace(/([A-Z])/g, ' $1')
                  .replace(/^./, str => str.toUpperCase())
                  .trim();
              } else if (value && typeof value === 'object') {
                const obj = value as Record<string, any>;
                profileName = obj.key || obj.name || obj.value || obj.profileName || '';
                displayLabel = obj.label || obj.displayName || profileName;
              } else {
                return null;
              }

              if (!profileName) return null;

              return { key: profileName, label: displayLabel };
            })
            .filter((item): item is { key: string; label: string } => item !== null);
        }
        return [];
      },
      providesTags: ['Samagam'],
      keepUnusedDataFor: 3600,
    }),

    /** KPI options for a selected category */
    getSamagamKPIsByCategory: build.query<Array<{ key: string; label: string }>, KPIByCategoryArgs>({
      query: ({ category }) => `/shared/kpicategorybyprofile?category=${category}`,
      transformResponse: (response: any) => {
        if (response?.success) {
          if (Array.isArray(response.data)) {
            return response.data.map((item: any) => ({
              key: item.kpiKey,
              label: item.kpiLabel,
            }));
          }
          if (response.data && typeof response.data === 'object') {
            return Object.entries(response.data).map(([key, label]) => ({
              key,
              label: String(label),
            }));
          }
        }
        return [];
      },
      providesTags: ['Samagam'],
    }),

    /** KPI data for selected category + KPI */
    getSamagamKPIData: build.query<any[] | null, KPIDataArgs>({
      query: ({ category, kpi }) =>
        `/kvis/kvprofile/kpicatdatabyprofile?category=${category}&kpi=${kpi}`,
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        return null;
      },
      providesTags: ['Samagam'],
    }),

    /** ROwise data (all regions → districts → KVs) */
    getSamagamRowiseData: build.query<any[], void>({
      query: () => '/kvis/kvprofile/rostoalldistrictstoallkvs',
      transformResponse: (response: any) => {
        if (response?.success && Array.isArray(response.data)) return response.data;
        return [];
      },
      providesTags: ['Samagam'],
    }),

    /** State-wise school details */
    getSamagamStateWiseSchools: build.query<any[], void>({
      query: () => '/kvis/statewisekvisdetails',
      transformResponse: (response: any) => {
        if (response?.success && response.data && Array.isArray(response.data.data)) {
          return response.data.data.map((state: any) => {
            const validSchools = (state.kvisDetails || []).filter(
              (school: any) => school.schoolName && school.schoolName.trim() !== ''
            );
            return {
              stateID: state.stateDetails?.stateID,
              stateName: state.stateDetails?.stateName,
              schoolCount: validSchools.length,
              kvisDetails: validSchools,
            };
          });
        }
        return [];
      },
      providesTags: ['Samagam'],
    }),

    /** Full school details by school ID */
    getSamagamSchoolFullDetails: build.query<any, string | number>({
      query: (schoolID) => `/kvis/schoolsfulldetail/${schoolID}`,
      transformResponse: (response: any) => {
        if (response?.success && response.data) return response.data;
        throw new Error(response?.message || 'Failed to fetch school full details');
      },
      providesTags: ['Samagam'],
    }),

    // ============================================
    // Infrastructure Endpoints
    // ============================================

    getSamagamTotalClassrooms: build.query<any, void>({
      query: () => '/kvis/totalclassroomskvs',
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        throw new Error(response?.message || 'Failed to fetch total classrooms');
      },
      providesTags: ['SamagamInfra'],
    }),

    getSamagamTotalLaboratories: build.query<any, void>({
      query: () => '/kvis/totallaboratoriessciencecomputerlabs',
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        throw new Error(response?.message || 'Failed to fetch total laboratories');
      },
      providesTags: ['SamagamInfra'],
    }),

    getSamagamTotalLibraries: build.query<any, void>({
      query: () => '/kvis/totallibraries',
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        throw new Error(response?.message || 'Failed to fetch total libraries');
      },
      providesTags: ['SamagamInfra'],
    }),

    getSamagamTotalMultipurposeHalls: build.query<any, void>({
      query: () => '/kvis/totalmultipurposehallsassemblyevents',
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        throw new Error(response?.message || 'Failed to fetch total multipurpose halls');
      },
      providesTags: ['SamagamInfra'],
    }),

    getSamagamClassroomsPmShriComparison: build.query<any, void>({
      query: () => '/kvis/classrooms-pmshri-nonpmshri',
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        throw new Error(response?.message || 'Failed to fetch PM-SHRI comparison data');
      },
      providesTags: ['SamagamInfra'],
    }),

    getSamagamTopSchoolsByInfra: build.query<any, TopSchoolsInfraArgs>({
      query: ({ wise = 'classroomsinfra', order = 'desc', page = 1 }) =>
        `/kvis/topschools-infra?wise=${wise}&order=${order}&page=${page}`,
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        throw new Error(response?.message || 'Failed to fetch top schools by infrastructure');
      },
      providesTags: ['SamagamInfra'],
    }),

    // ============================================
    // Enrollment Endpoints
    // ============================================

    getSamagamClasswiseEnrollment: build.query<any, void>({
      query: () => '/kvis/classwiseenrollpm-nonpmshri',
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        throw new Error(response?.message || 'Failed to fetch classwise enrollment');
      },
      providesTags: ['SamagamEnrollment'],
    }),

    getSamagamClasswiseEnrollmentPercentage: build.query<any, void>({
      query: () => '/kvis/classwiseenrollpm-nonpmshriperc',
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        throw new Error(response?.message || 'Failed to fetch classwise enrollment percentage');
      },
      providesTags: ['SamagamEnrollment'],
    }),

    getSamagamEnrollmentTrend: build.query<any, EnrollmentTrendArgs>({
      query: ({ years = 5 }) => `/kvis/enrollmenttrend-lastyears?year=${years}`,
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        throw new Error(response?.message || 'Failed to fetch enrollment trend');
      },
      providesTags: ['SamagamEnrollment'],
    }),

    getSamagamClasswiseHighestEnrollment: build.query<any, EnrollmentTrendArgs>({
      query: ({ years = 5 }) => `/kvis/classwisehighestenrollment?year=${years}`,
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        throw new Error(response?.message || 'Failed to fetch classwise highest enrollment');
      },
      providesTags: ['SamagamEnrollment'],
    }),

    getSamagamHighestEnrollmentSchools: build.query<any, void>({
      query: () => '/kvis/highestenrollmentschools',
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        throw new Error(response?.message || 'Failed to fetch highest enrollment schools');
      },
      providesTags: ['SamagamEnrollment'],
    }),

    getSamagamLowEnrollmentSchools: build.query<any, void>({
      query: () => '/kvis/schools-low-declining-enrollment',
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        throw new Error(response?.message || 'Failed to fetch low enrollment schools');
      },
      providesTags: ['SamagamEnrollment'],
    }),

    // ============================================
    // ICT Profile Endpoints
    // ============================================

    getSamagamTotalComputersICT: build.query<any, void>({
      query: () => '/kvis/totalcomputers-ict',
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        throw new Error(response?.message || 'Failed to fetch total computers');
      },
      providesTags: ['SamagamICT'],
    }),

    getSamagamEDigiLearningRooms: build.query<any, void>({
      query: () => '/kvis/edigilearningrooms',
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        throw new Error(response?.message || 'Failed to fetch e-learning rooms');
      },
      providesTags: ['SamagamICT'],
    }),

    getSamagamInteractivePanels: build.query<any, void>({
      query: () => '/kvis/interactivepaneltouchenabled',
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        throw new Error(response?.message || 'Failed to fetch interactive panels');
      },
      providesTags: ['SamagamICT'],
    }),

    getSamagamSmartTVs: build.query<any, void>({
      query: () => '/kvis/smarttvs',
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        throw new Error(response?.message || 'Failed to fetch smart TVs');
      },
      providesTags: ['SamagamICT'],
    }),

    getSamagamInternetConnected: build.query<any, void>({
      query: () => '/kvis/internetconnected',
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        throw new Error(response?.message || 'Failed to fetch internet connected schools');
      },
      providesTags: ['SamagamICT'],
    }),

    getSamagamTopSchoolsByICT: build.query<any, void>({
      query: () => '/kvis/topschoolsbyictinfrastructure',
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        throw new Error(response?.message || 'Failed to fetch top schools by ICT');
      },
      providesTags: ['SamagamICT'],
    }),

    // ============================================
    // Library Profile Endpoints
    // ============================================

    getSamagamLibrarySummary: build.query<LibrarySummaryResponse | null, void>({
      query: () => '/kvis/totalibrariesallkvs',
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        return null;
      },
      providesTags: ['SamagamLibrary'],
    }),

    getSamagamDigitalLibrarySummary: build.query<DigitalLibraryResponse | null, void>({
      query: () => '/kvis/totaldigitallibrary',
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        return null;
      },
      providesTags: ['SamagamLibrary'],
    }),

    getSamagamAutomatedSystemsLibrary: build.query<AutomatedSystemsResponse | null, void>({
      query: () => '/kvis/automatedsystemlibraries',
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        return null;
      },
      providesTags: ['SamagamLibrary'],
    }),

    getSamagamAvgBooksPerSchool: build.query<AvgBooksResponse | null, void>({
      query: () => '/kvis/avgbooksperschool',
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        return null;
      },
      providesTags: ['SamagamLibrary'],
    }),

    getSamagamBestTopTenLibraries: build.query<BestLibrary[] | null, void>({
      query: () => '/kvis/besttoptenlibrary',
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        return null;
      },
      providesTags: ['SamagamLibrary'],
    }),

    getSamagamLibrariesNeedingUpgrade: build.query<LibraryUpgrade[] | null, void>({
      query: () => '/kvis/librariesneedingupgrade',
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        return null;
      },
      providesTags: ['SamagamLibrary'],
    }),

    // ============================================
    // CWSN Profile Endpoints
    // ============================================

    getSamagamCwsnStudentSummary: build.query<CwsnStudentResponse | null, void>({
      query: () => '/kvis/totalcwsnstudentsallkvs',
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        return null;
      },
      providesTags: ['SamagamCWSN'],
    }),

    getSamagamCwsnTeacherSummary: build.query<CwsnTeacherResponse | null, void>({
      query: () => '/kvis/totalcwsntrainedteachers',
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        return null;
      },
      providesTags: ['SamagamCWSN'],
    }),

    getSamagamSchoolRampSummary: build.query<RampResponse | null, void>({
      query: () => '/kvis/totalschoolswithramps',
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        return null;
      },
      providesTags: ['SamagamCWSN'],
    }),

    // ============================================
    // NCC Profile Endpoints (raw KPI data)
    // ============================================

    getSamagamNCCKPIData: build.query<any[], { kpi: string }>({
      query: ({ kpi }) => `/kvis/kvprofile/kpicatdatabyprofile?category=nccProfile&kpi=${kpi}`,
      transformResponse: (response: any) => {
        if (response?.success) return response.data?.kpi_data || [];
        return [];
      },
      providesTags: ['SamagamNCC'],
    }),

    // ============================================
    // Sports / Regional Grounds Endpoints
    // ============================================

    getSamagamRegionalGrounds: build.query<RegionalGroundsResponse | null, void>({
      query: () => '/kvis/groundforregionalevents',
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        return null;
      },
      providesTags: ['Samagam'],
    }),

    getSamagamNationalGrounds: build.query<NationalGroundsResponse | null, void>({
      query: () => '/kvis/groundfornationalevents',
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        return null;
      },
      providesTags: ['Samagam'],
    }),

    getSamagamSportsByCategory: build.query<SportsByCategoryResponse | null, void>({
      query: () => '/kvis/databysport',
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        return null;
      },
      providesTags: ['Samagam'],
    }),

    getSamagamQualityData: build.query<QualityDataResponse | null, void>({
      query: () => '/kvis/databyquality',
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        return null;
      },
      providesTags: ['Samagam'],
    }),

    getSamagamIndoorOutdoorData: build.query<IndoorOutdoorResponse | null, void>({
      query: () => '/kvis/databyindooroutdoor',
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        return null;
      },
      providesTags: ['Samagam'],
    }),

    getSamagamTopFacilityKvs: build.query<TopFacilityKvsResponse | null, void>({
      query: () => '/kvis/topfacilitykvs',
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        return null;
      },
      providesTags: ['Samagam'],
    }),
    // ============================================
    // Green Building Endpoints
    // ============================================

    getSamagamGreenBuildingRainwater: build.query<GreenBuildingCategoryResponse | null, void>({
      query: () => '/kvis/rowisegreenbuildingsummary?category=isRainWaterSystem',
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        return null;
      },
      providesTags: ['Samagam'],
    }),

    getSamagamGreenBuildingSolar: build.query<GreenBuildingCategoryResponse | null, void>({
      query: () => '/kvis/rowisegreenbuildingsummary?category=solarPowerPlant',
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        return null;
      },
      providesTags: ['Samagam'],
    }),

    getSamagamGreenBuildingLED: build.query<GreenBuildingCategoryResponse | null, void>({
      query: () => '/kvis/rowisegreenbuildingsummary?category=energyEfficientElectricSystem',
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        return null;
      },
      providesTags: ['Samagam'],
    }),

    getSamagamGreenBuildingSolarLights: build.query<GreenBuildingCategoryResponse | null, void>({
      query: () => '/kvis/rowisegreenbuildingsummary?category=availSolarLight',
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        return null;
      },
      providesTags: ['Samagam'],
    }),

    // ============================================
    // Safety Analysis Endpoints
    // ============================================
    getSamagamSafetyTotalDetails: build.query<SafetyTotalDetailsResponse | null, void>({
      query: () => '/kvis/safetyanalysistotaldetails',
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        return null;
      },
      providesTags: ['Samagam'],
    }),

    getSamagamSafetyCertificateDetails: build.query<SafetyCertificateDetailsResponse | null, void>({
      query: () => '/kvis/buildingsafetywisecertificatedetails',
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        return null;
      },
      providesTags: ['Samagam'],
    }),

    // ============================================
    // Special Labs Endpoints
    // ============================================
    getSamagamSpecialLabsDetails: build.query<SpecialLabsDetailsResponse | null, { category: string }>({
      query: ({ category }) => `/kvis/speciallabsanalysis?category=${category}`,
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        return null;
      },
      providesTags: ['Samagam'],
    }),

    // ============================================
    // NCC Profile Endpoints
    // ============================================
    getSamagamNCCProfileDetails: build.query<NCCProfileDetailsResponse | null, { category: string }>({
      query: ({ category }) => `/kvis/nccprofiledetails?category=${category}`,
      transformResponse: (response: any) => {
        if (response?.success) return response.data;
        return null;
      },
      providesTags: ['Samagam'],
    }),
  }),
  overrideExisting: false,
});

// Export auto-generated hooks
export const {
  // Core
  useGetSamagamTotalSchoolsQuery,
  useGetSamagamAllStatesQuery,
  useGetSamagamAllRegionsQuery,
  useGetSamagamAllCategoriesQuery,
  useGetSamagamKPIsByCategoryQuery,
  useGetSamagamKPIDataQuery,
  useGetSamagamRowiseDataQuery,
  useGetSamagamStateWiseSchoolsQuery,
  useGetSamagamSchoolFullDetailsQuery,
  // Infrastructure
  useGetSamagamTotalClassroomsQuery,
  useGetSamagamTotalLaboratoriesQuery,
  useGetSamagamTotalLibrariesQuery,
  useGetSamagamTotalMultipurposeHallsQuery,
  useGetSamagamClassroomsPmShriComparisonQuery,
  useGetSamagamTopSchoolsByInfraQuery,
  // Enrollment
  useGetSamagamClasswiseEnrollmentQuery,
  useGetSamagamClasswiseEnrollmentPercentageQuery,
  useGetSamagamEnrollmentTrendQuery,
  useGetSamagamClasswiseHighestEnrollmentQuery,
  useGetSamagamHighestEnrollmentSchoolsQuery,
  useGetSamagamLowEnrollmentSchoolsQuery,
  // ICT
  useGetSamagamTotalComputersICTQuery,
  useGetSamagamEDigiLearningRoomsQuery,
  useGetSamagamInteractivePanelsQuery,
  useGetSamagamSmartTVsQuery,
  useGetSamagamInternetConnectedQuery,
  useGetSamagamTopSchoolsByICTQuery,
  // Library
  useGetSamagamLibrarySummaryQuery,
  useGetSamagamDigitalLibrarySummaryQuery,
  useGetSamagamAutomatedSystemsLibraryQuery,
  useGetSamagamAvgBooksPerSchoolQuery,
  useGetSamagamBestTopTenLibrariesQuery,
  useGetSamagamLibrariesNeedingUpgradeQuery,
  // CWSN
  useGetSamagamCwsnStudentSummaryQuery,
  useGetSamagamCwsnTeacherSummaryQuery,
  useGetSamagamSchoolRampSummaryQuery,
  // NCC
  useGetSamagamNCCKPIDataQuery,
  // Sports
  useGetSamagamRegionalGroundsQuery,
  useGetSamagamNationalGroundsQuery,
  useGetSamagamSportsByCategoryQuery,
  useGetSamagamQualityDataQuery,
  useGetSamagamIndoorOutdoorDataQuery,
  useGetSamagamTopFacilityKvsQuery,
  // Green Building
  useGetSamagamGreenBuildingRainwaterQuery,
  useGetSamagamGreenBuildingSolarQuery,
  useGetSamagamGreenBuildingLEDQuery,
  useGetSamagamGreenBuildingSolarLightsQuery,
  // Safety
  useGetSamagamSafetyTotalDetailsQuery,
  useGetSamagamSafetyCertificateDetailsQuery,
  // Special Labs
  useGetSamagamSpecialLabsDetailsQuery,
  // NCC
  useGetSamagamNCCProfileDetailsQuery,
} = kvsSamagamApi;
